/**
 * order.v1 cizici — gorselli SIRALAMA. Alttaki KARISIK kart havuzundan cocuk
 * kartlara dogru sirayla dokunur; her dokunuş ustteki sira seridine 1,2,3...
 * olarak eklenir. Seritteki bir karta dokunmak onu havuza geri gonderir ve
 * kalanlar otomatik yeniden numaralanir.
 *
 * Icerik (client): { prompt?, items:[{id,imageUrl}] }  (dogru sira `order`
 *   yalnizca authoring'de; client'a gelmez. Havuz runtime'da karistirilir.)
 * Cevap: onAnswerChange({ order:[id,...] }); tum kartlar dizilene kadar null.
 *   Skorlama SUNUCUDA (cocugun sirasi ~ item.order).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { MechanicProps } from "./types";

interface OrderCard {
  id: string;
  imageUrl?: string;
}
interface OrderContent {
  prompt?: { text?: string; subText?: string; story?: string };
  items?: OrderCard[];
  /** true: havuz+slot yerine TEK liste; kartlar sürüklenerek yeniden sıralanır. */
  reorder?: boolean;
}

export function OrderItem({ item, onAnswerChange }: MechanicProps) {
  const c = item.content as unknown as OrderContent;
  const items = c.items ?? [];
  const reorder = !!c.reorder;
  const byId: Record<string, OrderCard> = {};
  items.forEach((it) => (byId[it.id] = it));

  // havuzu bir kez karistir (soru degisince yeniden)
  const shuffled = useMemo(() => {
    const a = items.map((x) => x.id);
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.itemId]);

  const saved = item.answer?.order as string[] | undefined;
  const [order, setOrder] = useState<string[]>(saved ?? (reorder ? shuffled : []));
  const [dragId, setDragId] = useState<string | null>(null);

  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    const done = items.length > 0 && order.length === items.length;
    cb.current(done ? { order } : null, order.length > 0 ? { order } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // reorder modu: kartı sürükleyip listedeki başka kartın üzerine gelince yer değiştir.
  useEffect(() => {
    if (!dragId) return;
    const move = (e: PointerEvent) => {
      const el = (document.elementFromPoint(e.clientX, e.clientY) as Element | null)?.closest("[data-oid]");
      const overId = el?.getAttribute("data-oid");
      if (overId && overId !== dragId) {
        setOrder((o) => {
          const from = o.indexOf(dragId);
          const to = o.indexOf(overId);
          if (from === -1 || to === -1) return o;
          const na = o.slice();
          na.splice(from, 1);
          na.splice(to, 0, dragId);
          return na;
        });
      }
    };
    const up = () => setDragId(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [dragId]);

  const append = (id: string) => setOrder((o) => (o.includes(id) ? o : [...o, id]));
  const remove = (id: string) => setOrder((o) => o.filter((x) => x !== id));

  const pool = shuffled.filter((id) => !order.includes(id));

  // ---- reorder modu: TEK liste, sürükle-bırak ile yeniden sırala ----
  if (reorder) {
    return (
      <div className="mech order-mech order-reorder">
        {c.prompt?.text && <h2 className="prompt">{c.prompt.text}</h2>}
        {c.prompt?.subText && <p className="prompt-sub">{c.prompt.subText}</p>}
        {c.prompt?.story && (
          <div className="order-story">
            {c.prompt.story.split("\n\n").map((par, i) => (
              <p key={i}>{par}</p>
            ))}
          </div>
        )}
        <div className="order-sort">
          {order.map((id, i) => {
            const card = byId[id];
            if (!card) return null;
            return (
              <div
                className={`order-sort-card${dragId === id ? " dragging" : ""}`}
                key={id}
                data-oid={id}
                onPointerDown={(e) => { e.preventDefault(); setDragId(id); }}
              >
                <span className="order-badge">{i + 1}</span>
                {card.imageUrl && <img src={card.imageUrl} alt="" draggable={false} />}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mech order-mech">
      {c.prompt?.text && <h2 className="prompt">{c.prompt.text}</h2>}
      {c.prompt?.subText && <p className="prompt-sub">{c.prompt.subText}</p>}

      {c.prompt?.story && (
        <div className="order-story">
          {c.prompt.story.split("\n\n").map((par, i) => (
            <p key={i}>{par}</p>
          ))}
        </div>
      )}

      {/* ust: sira seridi (dolan slotlar) */}
      <div className="order-seq">
        {items.map((_, i) => {
          const id = order[i];
          const card = id ? byId[id] : null;
          return (
            <div className={`order-slot${card ? " filled" : ""}`} key={i}>
              <span className="order-badge">{i + 1}</span>
              {card ? (
                <button
                  type="button"
                  className="order-card in-seq"
                  onClick={() => remove(card.id)}
                  title="havuza geri koy"
                >
                  {card.imageUrl && <img src={card.imageUrl} alt="" draggable={false} />}
                </button>
              ) : (
                <span className="order-empty" />
              )}
            </div>
          );
        })}
      </div>

      <hr className="q-divider" />

      {/* alt: karisik havuz */}
      <div className="order-pool">
        {pool.map((id) => {
          const card = byId[id];
          if (!card) return null;
          return (
            <button
              type="button"
              key={id}
              className="order-card"
              onClick={() => append(id)}
            >
              {card.imageUrl && <img src={card.imageUrl} alt="" draggable={false} />}
            </button>
          );
        })}
        {pool.length === 0 && <span className="order-pool-empty">Tüm kartlar sıralandı.</span>}
      </div>
    </div>
  );
}
